import { useState } from "react";
import { motion } from "framer-motion";
import { VIN } from "@/constants/testIds";
import { ScanLine, ArrowRight } from "lucide-react";

const MOCK_RESULTS = {
  "1HGCM82633A123456": {
    year: "2003", make: "Honda", model: "Accord EX",
    engine: "3.0L V6 · J30A4", trans: "5-Speed Automatic",
    plant: "Marysville, Ohio · USA",
  },
  "WBAJA7C55KWW00000": {
    year: "2019", make: "BMW", model: "530i xDrive",
    engine: "2.0L Turbo I4 · B48", trans: "8-Speed Steptronic",
    plant: "Dingolfing · Germany",
  },
  "5YJ3E1EA7KF000000": {
    year: "2019", make: "Tesla", model: "Model 3 Long Range",
    engine: "Dual Motor AWD · 258kW", trans: "1-Speed Direct Drive",
    plant: "Fremont, CA · USA",
  },
};

export const VinLookup = () => {
  const [vin, setVin] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handle = (e) => {
    e.preventDefault();
    const v = vin.trim().toUpperCase();
    if (!v) return;
    setLoading(true);
    setResult(null);
    setTimeout(() => {
      const found = MOCK_RESULTS[v] || {
        year: "n/a", make: "Decoded", model: v.slice(0, 8) + "…",
        engine: "Signature matched · engine block ID pending",
        trans: "Auto-detected from VIN digit 8",
        plant: "Manufacturer plant · resolving",
      };
      setResult(found);
      setLoading(false);
    }, 700);
  };

  return (
    <section
      id="vin"
      data-testid={VIN.section}
      className="relative bg-obsidian py-24 lg:py-40 border-t border-white/5"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <span className="chip-brand">/ 05 · decode</span>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tighter leading-[0.95]">
              Type a VIN.<br />
              Meet your car.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-white/55 text-base font-light leading-relaxed">
              Try the demo. 17 characters, no spaces. Sample VINs are pre-loaded:
              <span className="font-mono-tech text-cyan-electric/80 block mt-2 text-xs">
                1HGCM82633A123456 · WBAJA7C55KWW00000 · 5YJ3E1EA7KF000000
              </span>
            </p>
          </div>
        </div>

        <form onSubmit={handle} className="relative">
          <div className="flex items-center gap-4 border-b border-white/15 focus-within:border-mclaren transition-colors py-4">
            <ScanLine className="h-5 w-5 text-white/40 shrink-0" strokeWidth={1.5} />
            <input
              data-testid={VIN.input}
              value={vin}
              onChange={(e) => setVin(e.target.value)}
              placeholder="Enter 17-character VIN"
              maxLength={17}
              className="flex-1 bg-transparent outline-none font-mono-tech tracking-widest text-white text-base sm:text-2xl lg:text-3xl uppercase placeholder:text-white/20"
            />
            <button
              data-testid={VIN.submit}
              type="submit"
              disabled={loading}
              className="group inline-flex items-center gap-3 h-12 px-6 bg-mclaren text-void font-mono-tech text-[11px] uppercase tracking-[0.2em] hover:bg-sunset transition-all disabled:opacity-50"
            >
              {loading ? "Decoding…" : "Decode"}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </form>

        {result && (
          <motion.div
            data-testid={VIN.result}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-10 border border-white/10 bg-void p-8 lg:p-10"
          >
            <div className="flex items-center gap-3 mb-8">
              <span className="h-1.5 w-1.5 bg-cyan-electric animate-pulse-dot" />
              <span className="font-mono-tech text-[10px] uppercase tracking-widest text-cyan-electric">
                Match found · 0.32s
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
              {[
                ["Year", result.year],
                ["Make", result.make],
                ["Model", result.model],
                ["Engine", result.engine],
                ["Transmission", result.trans],
              ].map(([k, v]) => (
                <div key={k} className="border-l border-white/10 pl-4">
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/40 mb-2">
                    {k}
                  </div>
                  <div className="font-display text-xl text-white font-medium tracking-tight leading-snug">
                    {v}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 pt-6 border-t border-white/10 font-mono-tech text-[11px] uppercase tracking-widest text-white/40">
              Plant · <span className="text-white/70">{result.plant}</span>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default VinLookup;
